// Realtor Plugin State
// Extracted from src/store/authSlice.ts

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type UserMode = 'agent' | 'client';

export interface RealtorDeepLinkParams {
  type: UserMode | null;
  refCode: string | null;
  agentId: string | null;
  action: string | null;
}

// Parse deep link from Telegram start_param
// Formats: realtor_agent, realtor_client_{agent_id}, realtor_ref_{code}
export function parseRealtorDeepLink(startParam: string): RealtorDeepLinkParams {
  const result: RealtorDeepLinkParams = {
    type: null,
    refCode: null,
    agentId: null,
    action: null,
  };

  if (!startParam) return result;

  const parts = startParam.split('_');
  if (parts.length < 2 || parts[0] !== 'realtor') return result;

  const action = parts[1];
  result.action = action;

  if (action === 'agent') {
    result.type = 'agent';
  } else if (action === 'client' && parts.length >= 3) {
    result.type = 'client';
    result.agentId = parts.slice(2).join('_');
  } else if (action === 'ref' && parts.length >= 3) {
    result.refCode = parts.slice(2).join('_');
  }

  return result;
}

interface RealtorState {
  userMode: UserMode;
  assignedAgentId: string | null;
  refCode: string | null;
  
  setUserMode: (mode: UserMode) => void;
  setAssignedAgentId: (agentId: string | null) => void;
  setRefCode: (code: string | null) => void;
  initFromDeepLink: (startParam: string) => void;
  reset: () => void;
}

export const useRealtorStore = create<RealtorState>()(
  persist(
    (set) => ({
      userMode: 'agent',
      assignedAgentId: null,
      refCode: null,
      
      setUserMode: (userMode) => set({ userMode }),
      setAssignedAgentId: (assignedAgentId) => set({ assignedAgentId }),
      setRefCode: (refCode) => set({ refCode }),
      
      initFromDeepLink: (startParam) => {
        const params = parseRealtorDeepLink(startParam);
        set({
          userMode: params.type || 'agent',
          assignedAgentId: params.agentId,
          refCode: params.refCode,
        });
      },
      
      reset: () => set({
        userMode: 'agent',
        assignedAgentId: null,
        refCode: null,
      }),
    }),
    {
      name: 'nre-realtor',
      partialize: (state) => ({
        userMode: state.userMode,
        assignedAgentId: state.assignedAgentId,
        refCode: state.refCode,
      }),
    }
  )
);
